import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Clock, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface EmailMetrics {
  total_sent: number;
  total_failed: number;
  smtp_count: number;
  resend_count: number;
  success_rate: number;
  rate_limit_hits: number;
}

interface RecentEmail {
  id: string;
  to_email: string;
  from_email: string;
  subject: string;
  status: string;
  provider_used: string;
  sent_at: string;
  error_message?: string;
  body?: string;
}

interface AuditEvent {
  id: string;
  event_type: string;
  created_at: string;
  details: any;
  user_id: string;
}

export const EmailMonitoring = () => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<EmailMetrics | null>(null);
  const [recentEmails, setRecentEmails] = useState<RecentEmail[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [hourlyData, setHourlyData] = useState<any[]>([]);
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.company_id) {
      loadData();
    }
  }, [profile?.company_id, timeRange]);

  const loadData = async () => {
    if (!profile?.company_id) return;
    
    setLoading(true);
    try {
      await Promise.all([
        loadMetrics(),
        loadRecentEmails(),
        loadAuditEvents(),
        loadHourlyData()
      ]);
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
    const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

    // Fetch email logs
    const { data: logs } = await supabase
      .from('email_logs')
      .select('status, provider_used')
      .eq('company_id', profile!.company_id)
      .gte('sent_at', since);

    // Fetch rate limit hits from audit logs
    const { data: rateLimitEvents } = await supabase
      .from('email_audit_logs')
      .select('id')
      .eq('company_id', profile!.company_id)
      .eq('event_type', 'rate_limit_exceeded')
      .gte('created_at', since);

    if (logs) {
      const sent = logs.filter(l => l.status === 'sent').length;
      const failed = logs.filter(l => l.status === 'failed').length;
      
      setMetrics({
        total_sent: sent,
        total_failed: failed,
        smtp_count: logs.filter(l => l.provider_used === 'smtp').length,
        resend_count: logs.filter(l => l.provider_used === 'resend').length,
        success_rate: sent + failed > 0 ? (sent / (sent + failed)) * 100 : 0,
        rate_limit_hits: rateLimitEvents?.length || 0,
      });
    }
  };

  const loadRecentEmails = async () => {
    const { data } = await supabase
      .from('email_logs')
      .select('id, to_email, from_email, subject, status, provider_used, sent_at, error_message, body')
      .eq('company_id', profile!.company_id)
      .order('sent_at', { ascending: false })
      .limit(50);

    if (data) {
      setRecentEmails(data as RecentEmail[]);
    }
  };

  const loadAuditEvents = async () => {
    const { data } = await supabase
      .from('email_audit_logs')
      .select('id, event_type, created_at, details, user_id')
      .eq('company_id', profile!.company_id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (data) {
      setAuditEvents(data as AuditEvent[]);
    }
  };

  const loadHourlyData = async () => {
    const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
    const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

    const { data: logs } = await supabase
      .from('email_logs')
      .select('sent_at, status')
      .eq('company_id', profile!.company_id)
      .gte('sent_at', since);

    if (logs) {
      // Group by hour
      const hourlyMap = new Map<string, { sent: number; failed: number }>();
      
      logs.forEach(log => {
        const hour = new Date(log.sent_at).toISOString().substring(0, 13);
        const existing = hourlyMap.get(hour) || { sent: 0, failed: 0 };
        
        if (log.status === 'sent') {
          existing.sent++;
        } else {
          existing.failed++;
        }
        
        hourlyMap.set(hour, existing);
      });

      const chartData = Array.from(hourlyMap.entries())
        .map(([hour, data]) => ({
          hour: new Date(hour + ':00:00').toLocaleString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            hour: 'numeric' 
          }),
          sent: data.sent,
          failed: data.failed,
        }))
        .sort((a, b) => a.hour.localeCompare(b.hour));

      setHourlyData(chartData);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Sent</Badge>;
      case 'failed':
        return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" /> Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getEventTypeBadge = (eventType: string) => {
    const colorMap: Record<string, string> = {
      send_success: 'default',
      send_failure: 'destructive',
      send_attempt: 'secondary',
      rate_limit_exceeded: 'destructive',
      smtp_tested: 'secondary',
    };
    
    return <Badge variant={colorMap[eventType] as any || 'secondary'}>{eventType.replace(/_/g, ' ')}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Email Monitoring</h2>
          <p className="text-muted-foreground">Track email delivery performance and audit logs</p>
        </div>
        <Select value={timeRange} onValueChange={(value: any) => setTimeRange(value)}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics?.success_rate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {metrics?.total_sent} sent, {metrics?.total_failed} failed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Emails</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(metrics?.total_sent || 0) + (metrics?.total_failed || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              In the last {timeRange}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rate Limits Hit</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics?.rate_limit_hits || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              50 emails/hour per user
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Primary Provider</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(metrics?.smtp_count || 0) > (metrics?.resend_count || 0) ? 'SMTP' : 'Resend'}
            </div>
            <p className="text-xs text-muted-foreground">
              SMTP: {metrics?.smtp_count || 0}, Resend: {metrics?.resend_count || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Volume Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Email Volume Over Time</CardTitle>
          <CardDescription>Hourly email sending volume</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sent" stroke="#10b981" strokeWidth={2} name="Sent" />
              <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} name="Failed" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Provider Distribution */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Provider Distribution</CardTitle>
            <CardDescription>Emails sent by provider</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'SMTP', value: metrics?.smtp_count || 0 },
                    { name: 'Resend', value: metrics?.resend_count || 0 },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#3b82f6" />
                  <Cell fill="#10b981" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Success vs Failure</CardTitle>
            <CardDescription>Email delivery outcomes</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={[
                  { name: 'Sent', count: metrics?.total_sent || 0, fill: '#10b981' },
                  { name: 'Failed', count: metrics?.total_failed || 0, fill: '#ef4444' },
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Emails */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Emails</CardTitle>
          <CardDescription>Last 10 email sending attempts</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Sender</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Sent At</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentEmails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No emails sent yet
                  </TableCell>
                </TableRow>
              ) : (
                recentEmails.map((email) => (
                  <>
                    <TableRow 
                      key={email.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedEmailId(expandedEmailId === email.id ? null : email.id)}
                    >
                      <TableCell className="font-medium">{email.to_email}</TableCell>
                      <TableCell className="text-sm">{email.from_email}</TableCell>
                      <TableCell className="max-w-xs truncate">{email.subject}</TableCell>
                      <TableCell>{getStatusBadge(email.status)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{email.provider_used}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(email.sent_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {expandedEmailId === email.id ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedEmailId === email.id && (
                      <TableRow key={`${email.id}-body`}>
                        <TableCell colSpan={7} className="bg-muted/30 p-0">
                          <div className="p-4 space-y-3">
                            {email.error_message && (
                              <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>{email.error_message}</AlertDescription>
                              </Alert>
                            )}
                            <div className="border rounded-md bg-background p-4 max-h-96 overflow-auto">
                              {email.body ? (
                                <div dangerouslySetInnerHTML={{ __html: email.body }} />
                              ) : (
                                <p className="text-muted-foreground text-sm italic">No email body available</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Audit Events */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
          <CardDescription>Recent email-related events</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event Type</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    No audit events yet
                  </TableCell>
                </TableRow>
              ) : (
                auditEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{getEventTypeBadge(event.event_type)}</TableCell>
                    <TableCell className="text-sm">
                      {event.details && (
                        <pre className="text-xs text-muted-foreground">
                          {JSON.stringify(event.details, null, 2).substring(0, 100)}...
                        </pre>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(event.created_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {metrics && metrics.rate_limit_hits > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {metrics.rate_limit_hits} rate limit{metrics.rate_limit_hits > 1 ? 's' : ''} hit in the last {timeRange}. 
            Consider adjusting sending patterns or increasing limits.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
